import React, {
  Dispatch,
  ForwardRefRenderFunction,
  ReactNode,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Box, BoxProps } from '@mantine/core'
import flattenChildren from 'react-keyed-flatten-children'

import useStyles from './index.styles'

export type BlockProps = {
  scrollingId: string
}

export type ScrollingBlockProps = {
  children: ReactNode
  disableScrollTo?: boolean
  onVisible?: (v: string) => void
  unsetHash?: boolean
} & BoxProps

export type HandlerProps = {
  setCanScroll: Dispatch<SetStateAction<boolean>>
}

const ScrollingBlocks: ForwardRefRenderFunction<
  HandlerProps,
  ScrollingBlockProps
> = (
  {
    children,
    disableScrollTo = false,
    onVisible,
    unsetHash,
    className,
    classNames,
    styles,
    ...rest
  },
  ref,
) => {
  const { classes, cx } = useStyles(undefined, {
    name: 'ScrollingBlocks',
    styles,
    classNames,
  })
  const [canScroll, setCanScroll] = useState(!disableScrollTo)
  const refs = useRef<HTMLDivElement[]>([])
  const idListArr = useRef<string[]>([])
  const idIndexMapping = useRef<{ [key in string]: number }>({})
  const observerRef = useRef<IntersectionObserver | null>(null)

  useImperativeHandle(ref, () => ({
    setCanScroll,
  }))

  useEffect(() => {
    setCanScroll(!disableScrollTo)
  }, [disableScrollTo])

  useEffect(() => {
    const observerOptions = {
      rootMargin: '-120px 0px 0px 0px',
    }
    const activeListArr: boolean[] = []
    idListArr.current.map(() => {
      activeListArr.push(false)
    })

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const curId = (entry.target as HTMLElement).dataset.idScroll || ''

        const curIdx = idIndexMapping.current?.[curId]
        activeListArr[curIdx] = entry.isIntersecting
      })

      const activeIndex = activeListArr.findIndex((i) => i)
      const activeBlock = idListArr.current[activeIndex] || ''
      onVisible?.(activeBlock)
      if (canScroll && !unsetHash) {
        activeBlock
          ? window.history.replaceState(
              null,
              '',
              window.location.pathname +
                window.location.search +
                `#${activeBlock}`,
            )
          : window.history.replaceState(
              {},
              '',
              window.location.pathname + window.location.search,
            )
      }
    }, observerOptions)
    refs.current.map((i) => observerRef.current?.observe(i))

    return () => {
      refs.current.map((i) => observerRef.current?.unobserve(i))
    }
  }, [canScroll])

  return (
    <Box className={cx(className, classes.root)} {...rest}>
      {flattenChildren(children).map((element: ReactNode, idx) => {
        if (React.isValidElement(element)) {
          const eleProps = element?.props as any
          const curIdScroll = eleProps['data-id-scroll'] || ''

          return (
            <Box
              ref={(el: HTMLDivElement) => {
                if (el && refs.current) {
                  refs.current[idx] = el
                  idListArr.current[idx] = curIdScroll
                  idIndexMapping.current[curIdScroll] = idx
                }
              }}
              data-id-scroll={curIdScroll}
              key={idx}
              {...(eleProps['data-quo-lead'] && {
                'data-quo-lead': eleProps['data-quo-lead'],
              })}
              {...(eleProps['data-lead-si'] && {
                'data-lead-si': eleProps['data-lead-si'],
              })}
            >
              {element}
            </Box>
          )
        }

        return null
      })}
    </Box>
  )
}

export default forwardRef(ScrollingBlocks)
